import axios from 'axios';
import { createAuthHeaders } from '../../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface StorageBucket {
  id: string;
  name: string;
  public: boolean;
  created_at: string;
  updated_at: string;
  file_size_limit?: number;
  allowed_mime_types?: string[];
}

export interface CreateBucketOptions {
  name: string;
  public?: boolean;
  fileSizeLimit?: number;
  allowedMimeTypes?: string[];
}

export interface UpdateBucketOptions {
  public?: boolean;
  fileSizeLimit?: number | null;
  allowedMimeTypes?: string[] | null;
}

export interface BucketPolicy {
  name: string;
  command: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE';
  definition: string | null;  // The actual policy condition (USING or WITH CHECK clause)
  appliedTo: string;  // Role: 'public' or 'authenticated'
}

export interface StorageFile {
  id: string | null;  // null for folders
  name: string;
  bucket_id?: string;
  owner?: string;
  created_at: string;
  updated_at: string;
  last_accessed_at?: string;
  metadata?: {
    size?: number;
    mimetype?: string;
    eTag?: string;
    cacheControl?: string;
    lastModified?: string;
    contentLength?: number;
    httpStatusCode?: number;
  } | null;  // null for folders
}

export const storageApi = {
  /**
   * List all storage buckets for an OptiDev Cloud project
   */
  async listBuckets(token: string, projectId: string): Promise<StorageBucket[]> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Create a new storage bucket
   */
  async createBucket(
    token: string,
    projectId: string,
    options: CreateBucketOptions
  ): Promise<StorageBucket> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets`,
      options,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * List files in a storage bucket with optional prefix filter
   */
  async listFiles(
    token: string,
    projectId: string,
    bucket: string,
    prefix?: string
  ): Promise<StorageFile[]> {
    const params = prefix ? { prefix } : {};
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}/files`,
      {
        headers: createAuthHeaders(token),
        params,
      }
    );
    return response.data;
  },

  /**
   * Delete a file from a storage bucket
   */
  async deleteFile(
    token: string,
    projectId: string,
    bucket: string,
    path: string
  ): Promise<void> {
    await axios.delete(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}/files`,
      {
        headers: createAuthHeaders(token),
        params: { path },
      }
    );
  },

  /**
   * Delete a storage bucket
   * Note: Bucket must be empty before deletion
   */
  async deleteBucket(
    token: string,
    projectId: string,
    bucket: string
  ): Promise<void> {
    await axios.delete(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}`,
      {
        headers: createAuthHeaders(token),
      }
    );
  },

  /**
   * Empty a storage bucket (delete all files)
   */
  async emptyBucket(
    token: string,
    projectId: string,
    bucket: string
  ): Promise<void> {
    await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}/empty`,
      {},
      {
        headers: createAuthHeaders(token),
      }
    );
  },

  /**
   * Update bucket properties
   */
  async updateBucket(
    token: string,
    projectId: string,
    bucket: string,
    options: UpdateBucketOptions
  ): Promise<void> {
    await axios.put(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}`,
      options,
      {
        headers: createAuthHeaders(token),
      }
    );
  },

  /**
   * Get a presigned URL for direct file upload to Supabase Storage
   * Use this URL to upload files directly (no base64 encoding needed)
   */
  async getPresignedUploadUrl(
    token: string,
    projectId: string,
    bucket: string,
    path: string
  ): Promise<{ signedUrl: string; path: string; token: string; bucket: string }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/presigned-upload-url`,
      { bucket, path },
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },

  /**
   * Upload file directly to Supabase using presigned URL
   * @param signedUrl - The presigned URL from getPresignedUploadUrl
   * @param file - The File object to upload
   */
  async uploadToPresignedUrl(signedUrl: string, file: File): Promise<void> {
    await axios.put(signedUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
  },

  /**
   * Get RLS policies for a specific bucket
   * Returns policy name, command (SELECT/INSERT/DELETE), and applied roles
   */
  async getBucketPolicies(
    token: string,
    projectId: string,
    bucket: string
  ): Promise<BucketPolicy[]> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/storage-dashboard/buckets/${bucket}/policies`,
      {
        headers: createAuthHeaders(token),
      }
    );
    return response.data;
  },
};
